import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from "react-query";
import apiClient from '../../../request/http-common';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export default function UserBillings({ userId }) {
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedBilling, setSelectedBilling] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    amount: '',
    type: '',
    description: ''
  });

  const { data: billings, refetch: getBillings } = useQuery(
    "user-billings",
    async () => {
      return await apiClient.get(`/api/show-billings/${userId}`);
    },
    {
      enabled: false,
      onSuccess: (res) => {
        if (res.data.status === 'success') {
          // Handle success
        }
      },
      onError: (err) => {
        const errMessage = err.response?.data?.message || 'Failed to fetch billings';
        localStorage.setItem('notification', JSON.stringify({ message: errMessage, type: 'error' }));
        window.dispatchEvent(new Event('newMessage'));
      }
    }
  );

  const { mutate: createBilling, isLoading: isCreating } = useMutation(
    async () => {
      return await apiClient.post('/api/store-billing', {
        ...formData,
        user_id: userId
      });
    },
    {
      onSuccess: (res) => {
        if (res.data.status === 'success') {
          handleClose();
          getBillings();
          localStorage.setItem('notification', JSON.stringify({ message: res.data.message, type: 'success' }));
          window.dispatchEvent(new Event('newMessage'));
        }
      },
      onError: (err) => {
        const errMessage = err.response?.data?.message || 'Failed to create billing';
        localStorage.setItem('notification', JSON.stringify({ message: errMessage, type: 'error' }));
        window.dispatchEvent(new Event('newMessage'));
      }
    }
  );

  const { mutate: updateBilling, isLoading: isUpdating } = useMutation(
    async () => {
      return await apiClient.post(`/api/update-billing/${selectedBilling?.id}`, {
        ...formData,
        user_id: userId
      });
    },
    {
      onSuccess: (res) => {
        if (res.data.status === 'success') {
          handleEditClose();
          getBillings();
          localStorage.setItem('notification', JSON.stringify({ message: res.data.message, type: 'success' }));
          window.dispatchEvent(new Event('newMessage'));
        }
      },
      onError: (err) => {
        const errMessage = err.response?.data?.message || 'Failed to update billing';
        localStorage.setItem('notification', JSON.stringify({ message: errMessage, type: 'error' }));
        window.dispatchEvent(new Event('newMessage'));
      }
    }
  );

  const { mutate: deleteBilling } = useMutation(
    async (id) => {
      return await apiClient.delete(`/api/delete-billing/${id}`);
    },
    {
      onSuccess: (res) => {
        if (res.data.status === 'success') {
          getBillings();
          localStorage.setItem('notification', JSON.stringify({ message: res.data.message, type: 'success' }));
          window.dispatchEvent(new Event('newMessage'));
        }
      },
      onError: (err) => {
        const errMessage = err.response?.data?.message || 'Failed to delete billing';
        localStorage.setItem('notification', JSON.stringify({ message: errMessage, type: 'error' }));
        window.dispatchEvent(new Event('newMessage'));
      }
    }
  );

  const { mutate: clearBilling } = useMutation(
    async (id) => {
      return await apiClient.post(`/api/clear-billing/${id}`);
    },
    {
      onSuccess: (res) => {
        if (res.data.status === 'success') {
          getBillings();
          localStorage.setItem('notification', JSON.stringify({ message: res.data.message, type: 'success' }));
          window.dispatchEvent(new Event('newMessage'));
        }
      },
      onError: (err) => {
        const errMessage = err.response?.data?.message || 'Failed to clear billing';
        localStorage.setItem('notification', JSON.stringify({ message: errMessage, type: 'error' }));
        window.dispatchEvent(new Event('newMessage'));
      }
    }
  );

  useEffect(() => {
    getBillings();
  }, [userId]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({
      title: '',
      amount: '',
      type: '',
      description: ''
    });
  };

  const handleEditClick = (billing) => {
    setSelectedBilling(billing);
    setFormData({
      title: billing.title || '',
      amount: billing.amount,
      type: billing.type,
      description: billing.description
    });
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setSelectedBilling(null);
    setFormData({
      title: '',
      amount: '',
      type: '',
      description: ''
    });
  };

  const handleSubmit = () => {
    createBilling();
  };

  const handleUpdate = () => {
    updateBilling();
  };

  const handleDelete = (id) => {
    deleteBilling(id);
  };

  const handleClear = (id) => {
    clearBilling(id);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">User Billings</Typography>
        <Button variant="contained" onClick={handleClickOpen}>
          Add Billing
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {billings?.data?.data?.map((billing) => (
              <TableRow key={billing.id}>
                <TableCell>{billing.title || 'No Title'}</TableCell>
                <TableCell>${billing.amount}</TableCell>
                <TableCell>{billing.type}</TableCell>
                <TableCell>{billing.description}</TableCell>
                <TableCell>
                  <Chip
                    label={billing.status}
                    color={billing.status === 'pending' ? 'warning' : 'success'}
                  />
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEditClick(billing)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(billing.id)}>
                    <DeleteIcon />
                  </IconButton>
                  {billing.status === 'pending' && (
                    <IconButton onClick={() => handleClear(billing.id)}>
                      <CheckCircleIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Add New Billing</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Billing Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              sx={{ mb: 2 }}
              helperText="This will appear as the email subject"
            />
            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type}
                label="Type"
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              >
                <MenuItem value="withdrawal">Withdrawal</MenuItem>
                <MenuItem value="investment">Investment</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={isCreating}>
            {isCreating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={handleEditClose}>
        <DialogTitle>Edit Billing</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Billing Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              sx={{ mb: 2 }}
              helperText="This will appear as the email subject"
            />
            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type}
                label="Type"
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              >
                <MenuItem value="withdrawal">Withdrawal</MenuItem>
                <MenuItem value="investment">Investment</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button onClick={handleUpdate} disabled={isUpdating}>
            {isUpdating ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 